import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import WebSocketClient from '../services/WebSocketClient';
import { getAgentApiUrl, getWebSocketUrl } from '../config/api';
import ConfirmDialog from '../components/ConfirmDialog';

/**
 * AI Chat page with session history sidebar.
 * Follows the same pattern as Brokers and Strategies pages.
 */
const AIChat = () => {
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [availableAgents, setAvailableAgents] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState('sat-coordinator');
    const [isLoadingAgents, setIsLoadingAgents] = useState(true);
    const [dialogConfig, setDialogConfig] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
    const messagesEndRef = useRef(null);
    const wsClient = useRef(null);

    // Fetch available agents on mount
    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const url = `${getAgentApiUrl()}/list`;
                console.log('Fetching agents from:', url);
                const response = await fetch(url);
                if (response.ok) {
                    const agents = await response.json();
                    console.log('Agents fetched successfully:', agents);
                    setAvailableAgents(agents);
                } else {
                    console.error('Failed to fetch agents, status:', response.status);
                    setAvailableAgents([
                        { name: 'sat-coordinator', displayName: 'SAT Coordinator', description: 'Default agent' }
                    ]);
                }
            } catch (err) {
                console.error('Error fetching agents:', err);
                setAvailableAgents([
                    { name: 'sat-coordinator', displayName: 'SAT Coordinator', description: 'Default agent' }
                ]);
            } finally {
                setIsLoadingAgents(false);
            }
        };
        fetchAgents();
    }, []);

    // Load sessions from localStorage on mount
    useEffect(() => {
        const savedSessions = JSON.parse(localStorage.getItem('chatSessions') || '[]');
        setSessions(savedSessions);
    }, []);

    // Save sessions to localStorage whenever they change
    useEffect(() => {
        if (sessions.length > 0) {
            localStorage.setItem('chatSessions', JSON.stringify(sessions));
        }
    }, [sessions]);

    // Connect to WebSocket
    const connectToAgent = (existingSessionId = null, agentName = null) => {
        const token = localStorage.getItem('token');
        if (!token) {
            setError('No authentication token found. Please login.');
            return;
        }

        if (wsClient.current) {
            wsClient.current.close();
        }

        wsClient.current = new WebSocketClient();

        // Register message handler
        const handleMessage = (message) => {
            switch (message.type) {
                case 'PARTIAL':
                    setMessages(prev => {
                        const last = prev[prev.length - 1];
                        if (last && last.role === 'assistant' && !last.finished) {
                            return [...prev.slice(0, -1), { ...last, content: last.content + message.content }];
                        } else {
                            return [...prev, { role: 'assistant', content: message.content, finished: false }];
                        }
                    });
                    break;

                case 'THINKING':
                    setMessages(prev => [...prev, { role: 'system', content: `💭 ${message.content}`, type: 'thinking' }]);
                    break;

                case 'TOOL_CALL':
                    setMessages(prev => [...prev, { role: 'system', content: message.content, type: 'tool' }]);
                    break;

                case 'FINISH':
                    setMessages(prev => {
                        const last = prev[prev.length - 1];
                        if (last && last.role === 'assistant') {
                            return [...prev.slice(0, -1), { ...last, finished: true }];
                        }
                        return prev;
                    });
                    setIsLoading(false);
                    break;

                case 'REQUIRE_PERMISSION':
                    setMessages(prev => [...prev, {
                        role: 'system',
                        content: `⚠️ Permission required: ${message.command}`,
                        type: 'permission',
                        commandId: message.commandId,
                        command: message.command
                    }]);
                    break;

                case 'ERROR':
                    setError(message.content);
                    setIsLoading(false);
                    break;

                case 'TOKEN_REFRESH_NEEDED':
                    console.log('Token refresh needed');
                    break;

                default:
                    break;
            }
        };

        wsClient.current.onMessage(handleMessage);

        // Connect
        const agent = agentName || selectedAgent;
        wsClient.current.connect(token, agent, existingSessionId)
            .then(newSessionId => {
                setSessionId(newSessionId);
                setIsConnected(true);

                // Add to sessions list if new
                if (!existingSessionId) {
                    const newSession = {
                        id: newSessionId,
                        title: 'New Chat',
                        createdAt: new Date().toISOString(),
                        lastMessage: 'Started conversation'
                    };
                    setSessions(prev => [newSession, ...prev]);
                    setSelectedSession(newSession);
                }

                setMessages([{ role: 'system', content: `Connected to ${agent}`, type: 'system' }]);
            })
            .catch(err => {
                setError(`Connection failed: ${err.message}`);
                setIsConnected(false);
            });
    };

    // Cleanup on unmount (no auto-connect - user must select agent and click Connect)
    useEffect(() => {
        return () => {
            if (wsClient.current) {
                wsClient.current.close();
            }
        };
    }, []);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSendMessage = (e) => {
        e.preventDefault();

        if (!inputText.trim() || !isConnected || isLoading) {
            return;
        }

        // Add user message to chat
        const userMessage = { role: 'user', content: inputText };
        setMessages(prev => [...prev, userMessage]);

        // Update session's last message
        if (selectedSession) {
            setSessions(prev => prev.map(s =>
                s.id === selectedSession.id
                    ? { ...s, lastMessage: inputText.substring(0, 50), title: inputText.substring(0, 30) + '...' }
                    : s
            ));
        }

        // Send to WebSocket
        wsClient.current.sendMessage(inputText);

        setInputText('');
        setIsLoading(true);
    };

    const handlePermissionResponse = (commandId, granted) => {
        wsClient.current.sendPermissionResponse(commandId, granted);
        setMessages(prev => prev.map(msg =>
            msg.commandId === commandId
                ? { ...msg, content: `${msg.content} - ${granted ? '✅ Granted' : '❌ Denied'}` }
                : msg
        ));
    };

    const handleDisconnect = () => {
        if (wsClient.current) {
            wsClient.current.close();
        }
        setIsConnected(false);
        setSessionId(null);
        setMessages(prev => [...prev, { role: 'system', content: 'Disconnected from agent', type: 'system' }]);
    };

    const handleNewChat = () => {
        if (isConnected) {
            handleDisconnect();
        }
        setMessages([]);
        setSelectedSession(null);
        connectToAgent();
    };

    const handleSelectSession = (session) => {
        setSelectedSession(session);
        setMessages([]);
        connectToAgent(session.id);
    };

    const handleDeleteSession = (sessionId, e) => {
        e.stopPropagation();
        setDialogConfig({
            isOpen: true,
            title: 'Delete Session',
            message: 'Are you sure you want to delete this chat session?',
            onConfirm: () => {
                setDialogConfig({ isOpen: false });
                setSessions(prev => prev.filter(s => s.id !== sessionId));
                if (selectedSession?.id === sessionId) {
                    handleNewChat();
                }
            }
        });
    };

    const closeDialog = () => setDialogConfig({ isOpen: false });

    return (
        <DashboardLayout>
            <div className="flex h-[calc(100vh-4rem)] gap-4">
                {/* Session History Sidebar */}
                <div className="w-64 bg-base-100 rounded-lg shadow-lg p-4 flex flex-col">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold">Chat History</h2>
                        <button
                            className="btn btn-primary btn-sm btn-circle"
                            onClick={handleNewChat}
                            title="New Chat"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>

                    {/* Sessions List */}
                    <div className="flex-1 overflow-y-auto space-y-2">
                        {sessions.length === 0 ? (
                            <div className="text-center text-base-content/50 text-sm mt-8">
                                No chat history yet
                            </div>
                        ) : (
                            sessions.map(session => (
                                <div
                                    key={session.id}
                                    className={`card bg-base-200 cursor-pointer hover:bg-base-300 transition-colors ${selectedSession?.id === session.id ? 'ring-2 ring-primary' : ''
                                        }`}
                                    onClick={() => handleSelectSession(session)}
                                >
                                    <div className="card-body p-3">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-semibold text-sm truncate">{session.title}</h3>
                                                <p className="text-xs text-base-content/60 truncate">{session.lastMessage}</p>
                                                <p className="text-xs text-base-content/40 mt-1">
                                                    {new Date(session.createdAt).toLocaleDateString()}
                                                </p>
                                            </div>
                                            <button
                                                className="btn btn-ghost btn-xs btn-circle"
                                                onClick={(e) => handleDeleteSession(session.id, e)}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Chat Area */}
                <div className="flex-1 flex flex-col bg-base-100 rounded-lg shadow-lg">
                    {/* Chat Header */}
                    <div className="navbar bg-gradient-to-r from-primary to-secondary text-primary-content rounded-t-lg">
                        <div className="flex-1">
                            <h2 className="text-xl font-bold">SAT Agent Chat</h2>
                        </div>
                        <div className="flex-none gap-2 flex items-center">
                            {/* Agent Selection Dropdown */}
                            <div className="form-control">
                                <select
                                    className="select select-bordered select-sm bg-base-100 text-base-content"
                                    value={selectedAgent}
                                    onChange={(e) => setSelectedAgent(e.target.value)}
                                    disabled={isLoadingAgents || isConnected}
                                >
                                    {isLoadingAgents ? (
                                        <option>Loading agents...</option>
                                    ) : (
                                        availableAgents.map(agent => (
                                            <option key={agent.name} value={agent.name}>
                                                {agent.displayName}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>

                            {/* Connect/Disconnect Button */}
                            {isConnected ? (
                                <button
                                    className="btn btn-sm btn-error"
                                    onClick={handleDisconnect}
                                >
                                    Disconnect
                                </button>
                            ) : (
                                <button
                                    className="btn btn-sm btn-success"
                                    onClick={() => connectToAgent()}
                                    disabled={isLoadingAgents}
                                >
                                    Connect
                                </button>
                            )}

                            <div className={`badge ${isConnected ? 'badge-success' : 'badge-error'} gap-2`}>
                                <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-success-content animate-pulse' : 'bg-error-content'}`}></div>
                                {isConnected ? 'Connected' : 'Disconnected'}
                            </div>
                            {sessionId && (
                                <div className="badge badge-ghost font-mono text-xs">
                                    {sessionId.substring(0, 8)}...
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Error Alert */}
                    {error && (
                        <div className="alert alert-error shadow-lg m-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{error}</span>
                            <button className="btn btn-sm btn-ghost" onClick={() => setError(null)}>✕</button>
                        </div>
                    )}

                    {/* Messages Container */}
                    <div className="flex-1 overflow-y-auto p-4 bg-base-200">
                        {messages.map((msg, index) => (
                            <div key={index} className={`chat ${msg.role === 'user' ? 'chat-end' : 'chat-start'} mb-4`}>
                                <div className="chat-image avatar">
                                    <div className="w-10 rounded-full">
                                        {msg.role === 'user' ? (
                                            <div className="bg-primary text-primary-content w-10 h-10 rounded-full flex items-center justify-center font-bold">
                                                U
                                            </div>
                                        ) : msg.role === 'assistant' ? (
                                            <div className="bg-secondary text-secondary-content w-10 h-10 rounded-full flex items-center justify-center font-bold">
                                                AI
                                            </div>
                                        ) : (
                                            <div className="bg-accent text-accent-content w-10 h-10 rounded-full flex items-center justify-center font-bold">
                                                {msg.type === 'thinking' ? '💭' : msg.type === 'tool' ? '🔧' : 'S'}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="chat-header text-xs opacity-50 mb-1 flex items-center gap-2">
                                    {msg.role === 'user' ? 'You' : msg.role === 'assistant' ? 'Agent' : 'System'}
                                    {msg.type === 'thinking' && <span className="badge badge-info badge-xs">Thinking</span>}
                                    {msg.type === 'tool' && <span className="badge badge-warning badge-xs">Tool Call</span>}
                                    {msg.type === 'permission' && <span className="badge badge-error badge-xs">Permission Required</span>}
                                </div>

                                <div className={`chat-bubble ${msg.role === 'user'
                                    ? 'chat-bubble-primary'
                                    : msg.role === 'assistant'
                                        ? 'chat-bubble-secondary'
                                        : msg.type === 'thinking'
                                            ? 'chat-bubble-info'
                                            : msg.type === 'tool'
                                                ? 'chat-bubble-warning'
                                                : 'chat-bubble-accent'
                                    }`}>
                                    <div className="whitespace-pre-wrap break-words">{msg.content}</div>

                                    {msg.type === 'permission' && (
                                        <div className="flex gap-2 mt-3">
                                            <button
                                                className="btn btn-success btn-sm"
                                                onClick={() => handlePermissionResponse(msg.commandId, true)}
                                            >
                                                ✓ Allow
                                            </button>
                                            <button
                                                className="btn btn-error btn-sm"
                                                onClick={() => handlePermissionResponse(msg.commandId, false)}
                                            >
                                                ✕ Deny
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                        {isLoading && (
                            <div className="chat chat-start mb-4">
                                <div className="chat-image avatar">
                                    <div className="w-10 rounded-full">
                                        <div className="bg-secondary text-secondary-content w-10 h-10 rounded-full flex items-center justify-center font-bold">
                                            AI
                                        </div>
                                    </div>
                                </div>
                                <div className="chat-header text-xs opacity-50 mb-1">Agent</div>
                                <div className="chat-bubble chat-bubble-secondary">
                                    <span className="loading loading-dots loading-sm"></span>
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Form */}
                    <form onSubmit={handleSendMessage} className="p-4 bg-base-100 border-t border-base-300 rounded-b-lg">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                placeholder={isConnected ? "Type your message..." : "Connecting..."}
                                disabled={!isConnected || isLoading}
                                className="input input-bordered flex-1"
                            />
                            <button
                                type="submit"
                                disabled={!isConnected || isLoading || !inputText.trim()}
                                className="btn btn-primary"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                                </svg>
                                Send
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <ConfirmDialog
                isOpen={dialogConfig.isOpen}
                title={dialogConfig.title}
                message={dialogConfig.message}
                onConfirm={dialogConfig.onConfirm}
                onCancel={closeDialog}
            />
        </DashboardLayout>
    );
};

export default AIChat;
